import React from 'react';
import { Container, Box, Typography, Avatar, Fade } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Standardized Container with consistent spacing
const PageContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

// Styled header root with flex layout
const HeaderRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

// Enhanced page header with gradient title and optional icon
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, icon, actions }) => {
  const theme = useTheme();

  return (
    <HeaderRoot>
      {icon && (
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 56,
            height: 56,
            boxShadow: `0 8px 16px ${theme.palette.primary.main}66`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 12px 24px ${theme.palette.primary.main}99`,
              border: `2px solid ${theme.palette.primary.light}`,
            },
          }}
        >
          {icon}
        </Avatar>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant='h3'
          component='h1'
          fontWeight='bold'
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: subtitle ? 0.5 : 0,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant='subtitle1' color='text.secondary'>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
    </HeaderRoot>
  );
};

// Enhanced content section with consistent styling
export interface ContentSectionProps {
  title?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  sx?: any;
}

export const ContentSection: React.FC<ContentSectionProps> = ({ title, children, actions, sx = {} }) => {
  return (
    <Box sx={{ mb: 4, ...sx }}>
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          {title && (
            <Typography variant='h5' component='h2' fontWeight={600}>
              {title}
            </Typography>
          )}
          {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
        </Box>
      )}
      {children}
    </Box>
  );
};

// Main page layout wrapper with fade-in animation
export interface PageLayoutProps {
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export const PageLayout: React.FC<PageLayoutProps> = ({ children, maxWidth = 'xl' }) => (
  <PageContainer maxWidth={maxWidth}>
    <Fade in={true} timeout={300}>
      <Box>{children}</Box>
    </Fade>
  </PageContainer>
);
